/**
 * New calculation — /settings/calculations/new
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Save } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import {
  CalculationEditor,
  type CalculationFormValue,
} from '@/components/custom/calculation-editor'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/calculations/new')({
  component: NewCalculationPage,
})

function NewCalculationPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [form, setForm] = useState<CalculationFormValue>({
    key: '',
    name: '',
    description: '',
    moduleKey: null,
    inputs: [
      {
        key: 'order_qty',
        label: 'Order quantity',
        type: 'number',
        required: true,
      },
    ],
    body: `let sheet_count = ceil(order_qty / 500)\nreturn sheet_count`,
  })

  const createMutation = useMutation(
    orpc.extensions.calculations.create.mutationOptions({
      onSuccess: (calc) => {
        toast.success(`Calculation "${calc.name}" created`)
        void queryClient.invalidateQueries({
          queryKey: orpc.extensions.calculations.list.queryKey({ input: {} }),
        })
        void navigate({
          to: '/settings/calculations/$key',
          params: { key: calc.key },
        })
      },
      onError: (err) => toast.error(err.message),
    }),
  )

  const handleSave = () => {
    createMutation.mutate({
      key: form.key,
      name: form.name,
      description: form.description || undefined,
      moduleKey: form.moduleKey,
      inputs: form.inputs,
      body: form.body,
    })
  }

  return (
    <div className="container mx-auto py-6 px-4 max-w-4xl space-y-4">
      <Link
        to="/settings/calculations"
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to calculations
      </Link>

      <Card>
        <CardHeader>
          <CardTitle>New calculation</CardTitle>
        </CardHeader>
        <CardContent>
          <CalculationEditor value={form} onChange={setForm} />
          <div className="flex justify-end gap-2 pt-4 border-t mt-6">
            <Button
              type="button"
              onClick={handleSave}
              disabled={createMutation.isPending}
            >
              <Save className="h-4 w-4 mr-2" />
              {createMutation.isPending ? 'Creating…' : 'Create calculation'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
